/**
 * ONÉRO — Find My Fit (blocks/onero-fit-finder.liquid).
 *
 * Telas: computador = altura/peso/idade num passo; celular = uma pergunta por tela com régua de rolar.
 * Depois peito e barriga (camisas, polos, malhas) ou barriga, quadril e cintura (calças e shorts).
 * A conta estima peito, cintura e quadril do corpo e compara com a tabela do produto
 * (metacampo custom.size_chart, em cm/kg). Ordem do que vale:
 *   colunas "Fits Weight/Height" > peito da peça (+ cintura da peça, quando tem) > "Fits Body Chest".
 * As respostas ficam no navegador (localStorage "onero-fit-profile") e valem pra todos os produtos.
 */
(() => {
  if (window.oneroFitFinder) return;
  window.oneroFitFinder = true;

  const KEY = 'onero-fit-profile';
  const MOBILE = window.matchMedia('(max-width: 749px)');

  /* ---------------- perfil salvo ---------------- */
  const load = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return {}; }
  };
  const save = (p) => {
    try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) { /* sem storage */ }
  };

  /* ---------------- conta ---------------- */
  // Folga (cm) entre o corpo e a peça. Calibrada com o Find My Fit da Tailored Athlete (04/10):
  // 10 perfis na camisa social deles, comparados com a Envoy pelo colarinho.
  const EASE_CHEST = 9;
  const EASE_WAIST = 6;
  // Tabela americana de medida do CORPO (peito, cm): até 89 XS, 96 S, 104 M, 112 L, 119 XL, 127 XXL, 135 3XL.
  // Usada nas peças cuja tabela diz "US sizing" (o nome do tamanho vale mais que a medida do fornecedor).
  const US_TOPS = [['XS', 89], ['S', 96], ['M', 104], ['L', 112], ['XL', 119], ['XXL', 127], ['3XL', 135]];
  const usLetter = (chest) => (US_TOPS.find(([, top]) => chest < top) || ['4XL'])[0];
  const sameSize = (a, b) => {
    const norm = (s) => String(s).toUpperCase().replace(/^2XL$/, 'XXL').replace(/^XXXL$/, '3XL');
    return norm(a) === norm(b);
  };

  /** Medidas do corpo estimadas (cm) a partir de altura, peso, idade e formato. */
  const body = (p) => {
    const age = p.age || 30;
    const chestAdj = { narrower: -3, average: 0, broader: 4 }[p.chest] ?? 0;
    const stomAdj = { flatter: -4, average: 0, rounder: 7 }[p.stomach] ?? 0;
    const seatAdj = { flatter: -3, average: 0, rounder: 5 }[p.seat] ?? 0;
    return {
      // acima de 85 kg o peso vai mais pra cintura que pro peito
      chest: 106 + 0.7 * (Math.min(p.kg, 85) - 85) + 0.5 * Math.max(p.kg - 85, 0) - 0.2 * (p.cm - 176) + chestAdj + (stomAdj > 0 ? 1.5 : 0),
      waist: 92 + 0.85 * (p.kg - 85) - 0.35 * (p.cm - 176) + 0.15 * (age - 30) + stomAdj,
      hip: 102 + 0.55 * (p.kg - 85) - 0.15 * (p.cm - 176) + seatAdj,
    };
  };

  /** "<160" | "160-170" | "170" | 96 → [min, max] */
  const range = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    if (raw.startsWith('<')) return [-Infinity, parseFloat(raw.slice(1))];
    if (raw.startsWith('>')) return [parseFloat(raw.slice(1)), Infinity];
    const parts = raw.split('-').map((x) => parseFloat(x));
    if (parts.some(Number.isNaN)) return null;
    return [parts[0], parts[parts.length - 1]];
  };

  /** Primeira linha cujo teto passa do valor (no limite exato sobe um: "Size up"). */
  const rowFor = (rows, col, value) => {
    const ranges = rows.map((r) => range(r[col]));
    if (ranges.some((r) => !r)) return -1;
    const tops = ranges.map((r, i) => {
      if (r[0] !== r[1]) return r[1];
      const next = ranges[i + 1];
      return next ? (r[1] + next[0]) / 2 : Infinity;
    });
    const i = tops.findIndex((t) => value < t);
    return i === -1 ? rows.length : i;
  };

  /** Menor linha com a medida da peça >= necessária (1 cm de tolerância: a medida é feita à mão). */
  const rowMin = (rows, col, need) => {
    const i = rows.findIndex((r) => parseFloat(r[col]) + 1 >= need);
    return i === -1 ? rows.length : i;
  };

  const recommend = (chart, mode, p, sizeUp) => {
    const cols = (chart.columns || []).map((c) => String(c).toLowerCase().trim());
    const rows = chart.rows || [];
    if (!rows.length || !p.cm || !p.kg) return null;
    const col = (test) => cols.findIndex(test);
    const b = body(p);
    let idx = -1;

    if (mode === 'bottom') {
      let waistIn = p.waistIn;
      if (!waistIn) waistIn = Math.round(b.waist / 2.54 - 1); // tamanho de calça ≈ cintura do corpo − 1 pol
      idx = rows.findIndex((r) => parseFloat(r[0]) >= waistIn);
      if (idx === -1) idx = rows.length;
      const hipCol = col((c) => c === 'hip');
      if (hipCol > -1 && p.seat === 'rounder') idx = Math.max(idx, rowMin(rows, hipCol, b.hip + 4));
    } else {
      const wCol = col((c) => c.includes('weight'));
      const hCol = col((c) => c.includes('height'));
      const gChest = col((c) => c === 'chest');
      const gWaist = col((c) => c === 'waist');
      const bChest = col((c) => c.includes('body chest'));
      const usCol = col((c) => c === 'us size');
      if (wCol > -1) {
        idx = rowFor(rows, wCol, p.kg);
        if (hCol > -1) {
          const h = rowFor(rows, hCol, p.cm);
          if (h > idx) idx = Math.min(h, idx + 1);
        }
        if (p.chest === 'broader' || p.stomach === 'rounder') idx += 1;
      } else if (bChest > -1) {
        idx = rowFor(rows, bChest, b.chest);
        if (p.stomach === 'rounder') idx += 1;
      } else if (usCol > -1 || /us sizing/i.test(chart.note || '')) {
        // tamanho americano: acha a linha com o mesmo nome (ou a coluna "US Size")
        const letter = usLetter(b.chest);
        const order = US_TOPS.map(([s]) => s).concat('4XL');
        const want = order.indexOf(letter);
        const nameOf = (r) => (usCol > -1 ? r[usCol] : r[0]);
        idx = rows.findIndex((r) => sameSize(nameOf(r), letter));
        if (idx === -1) {
          // sem esse nome na tabela: o primeiro tamanho maior, ou o maior que tem
          idx = rows.findIndex((r) => order.findIndex((s) => sameSize(s, nameOf(r))) > want);
          if (idx === -1) idx = rows.length;
        }
      } else if (gChest > -1) {
        idx = rowMin(rows, gChest, b.chest + EASE_CHEST);
        if (gWaist > -1) idx = Math.max(idx, rowMin(rows, gWaist, b.waist + EASE_WAIST));
      } else if (hCol > -1) {
        idx = rowFor(rows, hCol, p.cm);
      }
    }
    if (idx < 0) return null;
    // Peças marcadas no bloco (hoje só a Envoy manga longa): 1 tamanho acima, pra errar pro lado folgado.
    if (sizeUp) idx += 1;
    const over = idx >= rows.length;
    if (over) idx = rows.length - 1;
    return { size: String(rows[idx][0]), over };
  };

  /* ---------------- passos ---------------- */
  const stepsFor = (mode) => {
    const intro = MOBILE.matches ? ['height', 'weight', 'age'] : ['details'];
    const body = mode === 'bottom' ? ['stomach', 'seat', 'waist'] : ['chest', 'stomach'];
    return [...intro, ...body, 'result'];
  };

  const answered = (step, p) => {
    switch (step) {
      case 'details': return !!(p.cm && p.kg);
      case 'height': return !!p.cm;
      case 'weight': return !!p.kg;
      case 'age': return !!p.age;
      case 'chest': return !!p.chest;
      case 'stomach': return !!p.stomach;
      case 'seat': return !!p.seat;
      case 'waist': return p.waistIn !== undefined;
      default: return false;
    }
  };

  /* ---------------- régua (celular) ---------------- */
  const ITEM = 44;
  const wheelValues = (kind, units) => {
    const out = [];
    if (kind === 'height') {
      if (units.h === 'metric') for (let c = 145; c <= 215; c++) out.push({ v: c, html: `<b>${c}</b> cm` });
      else for (let i = 56; i <= 84; i++) out.push({ v: i * 2.54, html: `<b>${Math.floor(i / 12)}</b>ft <b>${i % 12}</b>in` });
    } else if (kind === 'weight') {
      if (units.w === 'metric') for (let k = 40; k <= 180; k++) out.push({ v: k, html: `<b>${k}</b> kg` });
      else for (let l = 90; l <= 400; l++) out.push({ v: l / 2.2046, html: `<b>${l}</b> lbs` });
    } else {
      for (let a = 16; a <= 85; a++) out.push({ v: a, html: `<b>${a}</b> years` });
    }
    return out;
  };

  const nearest = (list, value) => {
    let best = 0;
    list.forEach((it, i) => { if (Math.abs(it.v - value) < Math.abs(list[best].v - value)) best = i; });
    return best;
  };

  /* ---------------- componente ---------------- */
  class FitFinder {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.offMode;
      this.handle = root.dataset.offHandle;
      this.sizeUp = root.dataset.offSizeUp === 'true';
      this.dialog = root.querySelector('dialog');
      this.form = root.querySelector('form');
      try { this.chart = JSON.parse(root.querySelector('[data-off-chart]').textContent); } catch (e) { this.chart = null; }
      this.units = { h: 'imperial', w: 'imperial', ...(load().units || {}) };
      this.defaultLabel = root.querySelector('[data-off-label]').innerHTML.trim();
      this.bind();
      this.updateLaunch();
    }

    get profile() { return load(); }

    setProfile(patch) {
      const p = { ...load(), ...patch, units: this.units };
      save(p);
      document.querySelectorAll('[data-off]').forEach((el) => el._off?.updateLaunch());
      return p;
    }

    bind() {
      const r = this.root;
      r.querySelector('[data-off-open]').addEventListener('click', () => this.open());
      r.querySelector('[data-off-close]').addEventListener('click', () => this.close());
      r.querySelector('[data-off-prev]').addEventListener('click', () => this.go(this.i - 1));
      this.loadingMs = 1600;
      r.querySelector('[data-off-next]').addEventListener('click', () => this.next());
      r.querySelector('[data-off-edit]').addEventListener('click', () => this.restart());
      r.querySelector('[data-off-restart]').addEventListener('click', () => this.restart());
      r.querySelector('[data-off-select]').addEventListener('click', () => this.select());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('off-lock'));
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.next(); });
      this.form.addEventListener('input', () => { this.root.querySelector('[data-off-error]').hidden = true; });
      this.form.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) { e.preventDefault(); this.next(); }
      });
      r.querySelectorAll('[data-answer]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const [k, v] = btn.dataset.answer.split(':');
          this.setProfile(k === 'waist' ? { waistIn: Number(v) } : { [k]: v });
          this.markAnswers();
          setTimeout(() => this.go(this.i + 1, true), 160);
        })
      );
      r.querySelectorAll('[data-unit]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const [k, v] = btn.dataset.unit.split(':');
          if (this.step === 'details') this.readDetails(false);
          this.units[k] = v;
          this.setProfile({});
          this.paintUnits();
          if (this.step === 'details') this.fillDetails();
          else this.buildWheel(this.step);
        })
      );
      MOBILE.addEventListener('change', () => { if (this.dialog.open) this.go(0); });
    }

    /* abre no 1º passo sem resposta (ou direto no resultado) */
    open() {
      this.steps = stepsFor(this.mode);
      const p = this.profile;
      // Só abre direto no resultado no produto em que a pessoa já fez o teste.
      // Nos outros começa do 1º passo, com as respostas já preenchidas.
      const doneHere = (p.done || []).includes(this.handle);
      let start = 0;
      if (doneHere) {
        start = this.steps.findIndex((s) => s !== 'result' && !answered(s, p));
        if (start === -1) start = this.steps.length - 1;
      }
      document.documentElement.classList.add('off-lock');
      this.dialog.showModal();
      this.go(start);
    }

    /** "Start over": apaga as respostas (mantém pés/cm e lb/kg) e volta pro 1º passo. */
    restart() {
      save({ units: this.units });
      document.querySelectorAll('[data-off]').forEach((el) => el._off?.updateLaunch());
      this.go(0);
    }

    close() {
      clearTimeout(this.loadingTimer);
      this.root.querySelector('.off__foot').hidden = false;
      this.dialog.close();
    }

    get step() { return this.steps?.[this.i]; }

    go(i, fromQuestion = false) {
      this.steps = this.steps || stepsFor(this.mode);
      this.i = Math.max(0, Math.min(i, this.steps.length - 1));
      clearTimeout(this.loadingTimer);
      if (fromQuestion && this.step === 'result') {
        // "Finding your best fit..." antes de mostrar o tamanho, como a Tailored Athlete
        this.form.querySelectorAll('[data-step]').forEach((s) => { s.hidden = s.dataset.step !== 'loading'; });
        this.root.querySelector('.off__foot').hidden = true;
        this.loadingTimer = setTimeout(() => {
          this.root.querySelector('.off__foot').hidden = false;
          this.go(this.i);
        }, this.loadingMs);
        return;
      }
      const step = this.step;
      this.form.querySelectorAll('[data-step]').forEach((s) => { s.hidden = s.dataset.step !== step; });
      this.form.scrollTop = 0;
      const isResult = step === 'result';
      const isOption = ['chest', 'stomach', 'seat', 'waist'].includes(step);
      const q = (sel) => this.root.querySelector(sel);
      q('[data-off-prev]').hidden = this.i === 0 || isResult;
      q('[data-off-next]').hidden = isResult || isOption;
      q('[data-off-edit]').hidden = !isResult;
      q('[data-off-select]').hidden = !isResult;
      q('[data-off-restart]').hidden = this.i === 0 || isResult;
      q('[data-off-next]').textContent = MOBILE.matches ? this.nextLabel(step) : 'Next';
      this.root.querySelector('[data-off-error]').hidden = true;
      this.paintDots();
      this.paintUnits();
      this.markAnswers();
      if (step === 'details') this.fillDetails();
      if (['height', 'weight', 'age'].includes(step)) this.buildWheel(step);
      if (isResult) this.showResult();
      const focus = this.form.querySelector(`[data-step="${step}"] input, [data-step="${step}"] .off__wheel, [data-step="${step}"] button`);
      if (focus && !MOBILE.matches) focus.focus({ preventScroll: true });
    }

    nextLabel(step) {
      return { height: 'Next', weight: 'Next', age: 'Next' }[step] || 'Next';
    }

    next() {
      const step = this.step;
      if (step === 'details' && !this.readDetails(true)) return;
      if (['height', 'weight', 'age'].includes(step)) this.readWheel(step);
      this.go(this.i + 1, true);
    }

    paintDots() {
      const dots = this.root.querySelector('[data-off-dots]');
      dots.innerHTML = this.steps.map((_, n) => `<span class="${n === this.i ? 'is-on' : ''}"></span>`).join('');
    }

    paintUnits() {
      this.root.querySelectorAll('[data-unit]').forEach((btn) => {
        const [k, v] = btn.dataset.unit.split(':');
        btn.setAttribute('aria-pressed', String(this.units[k] === v));
      });
      this.root.querySelectorAll('[data-show-unit]').forEach((el) => { el.hidden = el.dataset.showUnit !== this.units.h; });
      const w = this.root.querySelector('[data-w-unit]');
      if (w) w.textContent = this.units.w === 'metric' ? 'kg' : 'lbs';
    }

    markAnswers() {
      const p = this.profile;
      this.root.querySelectorAll('[data-answer]').forEach((btn) => {
        const [k, v] = btn.dataset.answer.split(':');
        const cur = k === 'waist' ? String(p.waistIn ?? '') : String(p[k] ?? '');
        btn.setAttribute('aria-pressed', String(cur === v));
      });
    }

    /* ---- passo único (computador) ---- */
    field(name) { return this.form.querySelector(`[name="${name}"]`); }

    fillDetails() {
      const p = this.profile;
      const set = (n, v) => { this.field(n).value = v === undefined || v === null || Number.isNaN(v) ? '' : v; };
      if (p.cm) {
        const tot = Math.round(p.cm / 2.54);
        set('ft', Math.floor(tot / 12)); set('in', tot % 12); set('cm', Math.round(p.cm));
      } else { set('ft', ''); set('in', ''); set('cm', ''); }
      set('w', p.kg ? Math.round(this.units.w === 'metric' ? p.kg : p.kg * 2.2046) : '');
      set('age', p.age || '');
    }

    readDetails(validate) {
      const num = (n) => parseFloat(String(this.field(n).value).replace(',', '.'));
      let cm;
      if (this.units.h === 'metric') cm = num('cm');
      else {
        const ft = num('ft');
        const inch = Number.isNaN(num('in')) ? 0 : num('in');
        cm = Number.isNaN(ft) ? NaN : (ft * 12 + inch) * 2.54;
      }
      const wv = num('w');
      const kg = this.units.w === 'metric' ? wv : wv / 2.2046;
      const age = num('age');
      const okH = cm >= 140 && cm <= 220;
      const okW = kg >= 40 && kg <= 185;
      const okA = Number.isNaN(age) || (age >= 14 && age <= 99);
      if (validate && !(okH && okW && okA)) {
        const err = this.root.querySelector('[data-off-error]');
        err.textContent = !okH ? 'Please enter your height.' : !okW ? 'Please enter your weight.' : 'Please enter a valid age.';
        err.hidden = false;
        return false;
      }
      const patch = {};
      if (okH) patch.cm = Math.round(cm * 10) / 10;
      if (okW) patch.kg = Math.round(kg * 10) / 10;
      if (okA && !Number.isNaN(age)) patch.age = Math.round(age);
      this.setProfile(patch);
      return true;
    }

    /* ---- régua (celular) ---- */
    buildWheel(kind) {
      const wheel = this.form.querySelector(`[data-wheel="${kind}"]`);
      const list = wheelValues(kind, this.units);
      wheel._list = list;
      wheel.innerHTML = `<ol>${list.map((it) => `<li><span>${it.html}</span></li>`).join('')}</ol>`;
      const p = this.profile;
      const cur = kind === 'height' ? p.cm || 175.3 : kind === 'weight' ? p.kg || 77.1 : p.age || 30;
      const idx = nearest(list, cur);
      requestAnimationFrame(() => {
        wheel.scrollTop = idx * ITEM;
        this.paintWheel(wheel);
      });
      if (!wheel._bound) {
        wheel._bound = true;
        let t;
        wheel.addEventListener('scroll', () => {
          this.paintWheel(wheel);
          clearTimeout(t);
          t = setTimeout(() => this.readWheel(kind), 120);
        }, { passive: true });
        wheel.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            wheel.scrollBy({ top: e.key === 'ArrowDown' ? ITEM : -ITEM, behavior: 'smooth' });
          }
        });
        wheel.addEventListener('click', (e) => {
          const li = e.target instanceof Element ? e.target.closest('li') : null;
          if (li) wheel.scrollTo({ top: [...li.parentNode.children].indexOf(li) * ITEM, behavior: 'smooth' });
        });
      }
    }

    paintWheel(wheel) {
      const i = Math.round(wheel.scrollTop / ITEM);
      wheel.querySelectorAll('li').forEach((li, n) => li.classList.toggle('is-on', n === i));
    }

    readWheel(kind) {
      const wheel = this.form.querySelector(`[data-wheel="${kind}"]`);
      const list = wheel?._list;
      if (!list) return;
      const it = list[Math.max(0, Math.min(list.length - 1, Math.round(wheel.scrollTop / ITEM)))];
      if (kind === 'height') this.setProfile({ cm: Math.round(it.v * 10) / 10 });
      else if (kind === 'weight') this.setProfile({ kg: Math.round(it.v * 10) / 10 });
      else this.setProfile({ age: it.v });
    }

    /* ---- resultado ---- */
    result() { return this.chart ? recommend(this.chart, this.mode, this.profile, this.sizeUp) : null; }

    showResult() {
      const res = this.result();
      const done = load().done || [];
      if (res && !done.includes(this.handle)) this.setProfile({ done: [...done, this.handle] });
      this.root.querySelectorAll('[data-off-size]').forEach((el) => { el.textContent = res ? res.size : '-'; });
      const note = this.root.querySelector('[data-off-note]');
      note.hidden = !res?.over;
      if (res?.over) note.textContent = "This is our largest size in this piece, so it may fit close. Message us and we'll help you choose.";
    }

    select() {
      const res = this.result();
      if (!res) return this.close();
      const section = this.root.closest('.shopify-section') || document;
      const picker = section.querySelector('variant-picker[ref="mainVariantPicker"]') || section.querySelector('variant-picker');
      const input = picker && [...picker.querySelectorAll('input[type="radio"]')].find(
        (el) => /size|tamanho/i.test(el.dataset.optionName || '') && el.value === res.size
      );
      if (input) {
        if (input.checked) window.oneroSizeGate?.pick(input);
        else input.click();
      }
      this.close();
    }

    updateLaunch() {
      const label = this.root.querySelector('[data-off-label]');
      const p = this.profile;
      // "Your size" só aparece no produto em que a pessoa fez o teste
      const res = (p.done || []).includes(this.handle) ? this.result() : null;
      label.innerHTML = res ? `Your size: <b>${res.size}</b> <u>Change</u>` : this.defaultLabel;
      this.root.querySelector('[data-off-open]').classList.toggle('is-done', !!res);
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('[data-off]').forEach((el) => {
      if (!el._off) { el._off = new FitFinder(el); el.classList.add('is-ready'); }
    });
  };

  // usado pelos testes locais
  window.oneroFitRecommend = (chart, mode, p, sizeUp) => recommend(chart, mode, p, sizeUp);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // o tema redesenha partes da página quando troca a cor: liga os botões novos
  new MutationObserver(() => { if (document.querySelector('[data-off]:not(.is-ready)')) init(); }).observe(document.documentElement, { childList: true, subtree: true });
})();
